import React from 'react';
import { LayoutContainer } from '../../atoms/LayoutContainer';
import { GradientBar } from '../../atoms/GradientBar';
import { Icon } from '../../atoms/Icon';
import { Link } from '../../atoms/Link';
import { Text } from '../../atoms/Text';
import { Button } from '../../atoms/Button';
import { FooterSection, FooterLink } from '../../molecules/FooterSection';
import { hostBase } from '../../helpers/hostBase';
import { Image } from '../../atoms/Image';
import heartInternetLogo from '../../assets/images/heart-internet-logo.png';
import './Footer.css';

export interface FooterLinkSection {
  title: string;
  links: FooterLink[];
}

export interface SocialLink {
  platform: 'facebook' | 'linkedin' | 'x';
  href: string;
}

export interface FooterProps {
  /**
   * Link sections to display (e.g. Services, Company)
   */
  sections: FooterLinkSection[];
  /**
   * Social media links
   */
  socialLinks: SocialLink[];
  /**
   * Blog link configuration
   */
  blogLink?: { label: string; href: string };
  /**
   * Domain to prefix for links. The empty string means same-origin: links
   * render path-relative and stay on the current host (see helpers/hostBase).
   */
  wwwDomain?: string;
  /**
   * Show gradient bar at top of footer
   */
  showGradientBar?: boolean;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
}

export const Footer: React.FC<FooterProps> = ({
  sections,
  socialLinks,
  blogLink,
  wwwDomain = 'www.heartinternet.uk',
  showGradientBar = true,
  className = '',
  style,
}) => {
  const year = new Date().getFullYear();
  const baseClass = 'hi-ui hi-ui-footer';
  const allClasses = `${baseClass} ${className}`.trim();
  const base = hostBase(wwwDomain);

  return (
    <footer id="hi-ui-footer" className={allClasses} style={style}>
      {showGradientBar && <GradientBar />}
      <LayoutContainer>
        <div className="hi-ui-footer__content">
          <div className="hi-ui-footer__logo">
            <Image src={heartInternetLogo} alt="Heart Internet" loading="eager" />
          </div>

          <div className="hi-ui-footer__sections">
            {sections.map((section) => (
              <FooterSection
                key={section.title}
                title={section.title}
                links={section.links}
                wwwDomain={wwwDomain}
              />
            ))}
          </div>

          <div className="hi-ui-footer__social">
            <div className="hi-ui-footer__social-icons">
              {socialLinks.map((link) => (
                <Link
                  key={link.platform}
                  href={link.href}
                  external
                  noUnderline
                  aria-label={link.platform}
                  className="hi-ui-footer__social-link"
                >
                  <Icon name={link.platform} size={35} />
                </Link>
              ))}
            </div>
            {/* New tab, matching the TopNav's BLOG link, which hi-ui already
                marks `external`. The blog is a WordPress site we don't serve —
                same origin once presales is `www`, but a different application
                behind it. That distinction is invisible to a router: with no
                target, next/link claims the click, probes for an RSC payload,
                404s and only then falls back to a real page load (measured
                2026-08-11). `target` makes next/link leave the click alone, so
                the browser goes straight there and the server — which is the
                only thing that knows who owns `/blog` — answers. Button adds
                `rel="noopener noreferrer"` for `_blank` itself. */}
            {blogLink && (
              <Button
                as="a"
                href={`${base}${blogLink.href}`}
                target="_blank"
                variant="success"
                size="small"
              >
                {blogLink.label}
              </Button>
            )}
          </div>
        </div>

        <div className="hi-ui-footer__legal">
          <div className="hi-ui-footer__legal-left">
            <Text variant="body-small" inline className="hi-ui-footer__copyright">
              {String.fromCharCode(169)} {year} Heart Internet Ltd. All rights reserved.
            </Text>
            <div className="hi-ui-footer__legal-links">
              <Link href={`${base}/terms`} noUnderline>
                Terms and conditions
              </Link>
              <span className="hi-ui-footer__legal-separator">-</span>
              <Link
                href="#"
                className="termly-display-preferences"
                noUnderline
                onClick={(e) => {
                  e.preventDefault();
                  // Termly consent modal - try multiple API methods
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const w = window as any;
                  if (w.displayPreferenceModal) {
                    w.displayPreferenceModal();
                  } else if (w.Termly?.displayPreferenceModal) {
                    w.Termly.displayPreferenceModal();
                  }
                }}
              >
                Consent Preferences
              </Link>
              <span className="hi-ui-footer__legal-separator">-</span>
              <Link href={`${base}/terms/heart-internet-privacy-statement`} noUnderline>
                Privacy Policy
              </Link>
              <span className="hi-ui-footer__legal-separator">-</span>
              <Link href={`${base}/privacy-centre`} noUnderline>
                Privacy Centre
              </Link>
              <span className="hi-ui-footer__legal-separator">-</span>
              <Link href={`${base}/sitemap`} noUnderline>
                Sitemap
              </Link>
            </div>
          </div>
          <div className="hi-ui-footer__legal-right">
            <Text variant="body-small" inline>
              VAT Number: 467683539
            </Text>
            <Text variant="body-small" inline>
              Company number: 15319281
            </Text>
          </div>
        </div>
      </LayoutContainer>
    </footer>
  );
};
